<div class="wrapper" [class.scanning]="batchTicketScanning">
  <div class="heading text-type-2 mb-[20px] flex flex-row justify-between items-center">
    <div class="title text-type-2">Acceptance record for {{ bidItemName }}</div>
    <mat-icon (click)="close()">close</mat-icon>
  </div>

  <!-- Hidden file input for batch ticket -->
  <input
    #batchTicketFileInput
    (change)="onBatchTicketFileSelected($event)"
    accept="image/*,.pdf"
    class="hidden"
    type="file"
  />

  <!-- Materials Accordions -->
  @for (material of materials; track material.material_id) {
    <div
      [class.is-expanded]="expandedMaterialId === material.material?.id"
      class="material-accordion"
    >
      <!-- Material Header -->
      <div
        (click)="toggleMaterial(material.material?.id)"
        class="material-header flex justify-between items-center cursor-pointer"
      >
        <div class="material-name text-type-8">{{ material.material?.name | sentenceCase }}</div>
        <mat-icon
          [svgIcon]="expandedMaterialId === material.material?.id ? 'chevron-up' : 'chevron-down'"
          class="chevron-icon"
        />
      </div>

      <!-- Material Content -->
      @if (expandedMaterialId === material.material?.id && getMsmId(material)) {
        @let msmId = getMsmId(material);
        <div class="material-content">

          <!-- Acceptance Records -->
          @for (record of formData[msmId] || []; track record.uuid; let ri = $index) {
            @let acceptanceForm = acceptanceForms[msmId][record.uuid];
            <div class="record-section">
              <!-- Record Header -->
              <div class="record-header flex justify-between items-center">
                <div class="record-title text-type-8">Record {{ ri + 1 }}</div>
                <mat-icon
                  (click)="deleteRecord(material, record.uuid)"
                  class="record-delete-icon cursor-pointer"
                  svgIcon="trash"
                />
              </div>

              <!-- Acceptance Record Fields -->
              @if (acceptanceFields.length && acceptanceForm) {
                <div class="acceptance-fields-section" [formGroup]="acceptanceForm">
                  @for (field of acceptanceFields; track field.id) {
                    <div class="acceptance-field" [class.mb-[30px]]="getFieldType(field) === 'display_only'" [class.mb-[40px]]="getFieldType(field) === 'boolean'" [class.mb-[20px]]="getFieldType(field) !== 'display_only' && getFieldType(field) !== 'boolean'">
                      @switch (getFieldType(field)) {
                        @case ('display_only') {
                          <app-view-field
                            [label]="field.label || field.name"
                            [uom]="field.uom || ''"
                            [value]="'' + (acceptanceForm?.controls[field.id]?.value ?? '')"
                          />
                        }
                        @case ('dropdown') {
                          <app-dropdown
                            [options]="getPickListOptions(field)"
                            [parentFormGroup]="acceptanceForm"
                            [controlName]="field.id"
                            [label]="field.label || field.name"
                            [sentenceCase]="true"
                            (checkForErrors)="onAcceptanceFieldChange(msmId, record.uuid, field.id)"
                          />
                        }
                        @case ('number') {
                          <app-number-input
                            [isDecimalAllowed]="true"
                            [decimalPlaces]="field.ui_config?.precision ?? 5"
                            [maxLength]="24"
                            [parentFormGroup]="acceptanceForm"
                            [controlName]="field.id"
                            [label]="(field.label || field.name) + (field.uom ? ' (' + field.uom + ')' : '')"
                            [suffixText]="field.uom || ''"
                            (checkForErrors)="onAcceptanceFieldChange(msmId, record.uuid, field.id)"
                          />
                        }
                        @case ('date') {
                          <app-date-picker
                            [parentFormGroup]="acceptanceForm"
                            [controlName]="field.id"
                            [label]="field.label || field.name"
                            (checkForErrors)="onAcceptanceFieldChange(msmId, record.uuid, field.id)"
                          />
                        }
                        @case ('time') {
                          <app-time-select
                            [parentFormGroup]="acceptanceForm"
                            [controlName]="field.id"
                            [timePeriodControlName]="'timePeriod_' + field.id"
                            [amPmControlName]="'amPm_' + field.id"
                            [label]="field.label || field.name"
                            (checkForErrors)="onAcceptanceFieldChange(msmId, record.uuid, field.id)"
                          />
                        }
                        @case ('boolean') {
                          <div class="boolean-field flex justify-between items-center">
                            <span class="boolean-label text-type-13">{{ field.label || field.name }}</span>
                            <mat-slide-toggle
                              [checked]="acceptanceForm?.controls[field.id]?.value"
                              (change)="acceptanceForm?.controls[field.id]?.setValue($event.checked); onAcceptanceFieldChange(msmId, record.uuid, field.id)"
                            />
                          </div>
                        }
                        @default {
                          <app-text-input
                            [parentFormGroup]="acceptanceForm"
                            [controlName]="field.id"
                            [label]="field.label || field.name"
                            (checkForErrors)="onAcceptanceFieldChange(msmId, record.uuid, field.id)"
                          />
                        }
                      }
                    </div>
                  }
                </div>
              }

              <!-- Scan Batch Ticket (materials with concrete groups) -->
              @if ((materialGroups[material.material?.id] || []).length) {
                <div class="scan-section mb-[20px]">
                  @if (isScanningRecord(record.uuid)) {
                    <div class="scan-loading flex items-center gap-[8px]">
                      <mat-spinner [diameter]="20" />
                      <span class="scan-loading-text">Scanning batch ticket...</span>
                    </div>
                  } @else {
                    <div (click)="triggerBatchTicketUpload(msmId, record.uuid)" class="scan-btn flex items-center gap-[8px] cursor-pointer">
                      <mat-icon class="scan-icon">photo_camera</mat-icon>
                      Scan batch ticket
                    </div>
                  }
                  @if (scannedFile && scannedFile.recordUuid === record.uuid) {
                    <div class="scanned-file-card mt-[12px] flex items-start gap-[12px]">
                      <div class="scanned-file-icon flex items-center justify-center">
                        <mat-icon>description</mat-icon>
                      </div>
                      <div class="scanned-file-info">
                        <div class="scanned-file-title">{{ scannedFile.fieldsAutofilled }} fields autofilled</div>
                        <div class="scanned-file-subtitle">Make sure to review them</div>
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Concrete Groups -->
              @for (group of materialGroups[material.material?.id] || []; track group.id; let gi = $index) {
                <div class="concrete-group-section">
                  @for (section of getSortedSections(group); track section.name) {
                    <div
                      [attr.data-section-key]="record.uuid + ':' + gi + ':' + section.name"
                      class="group-section"
                    >
                      <!-- Section Header (accordion) -->
                      <div
                        (click)="toggleGroupSection(record.uuid, gi, section.name)"
                        class="group-section-header flex justify-between items-center cursor-pointer"
                      >
                        <div class="group-section-name text-type-8 flex items-center gap-[8px]">
                          {{ formatSectionName(section) | sentenceCase }}
                          @if (isSectionAutofilled(record.uuid, section.name)) {
                            <span class="autofilled-badge">Autofilled</span>
                          }
                        </div>
                        <mat-icon
                          [svgIcon]="isGroupSectionExpanded(record.uuid, gi, section.name) ? 'chevron-up' : 'chevron-down'"
                          class="chevron-icon"
                        />
                      </div>

                      <!-- Section Fields -->
                      @if (isGroupSectionExpanded(record.uuid, gi, section.name)) {
                        <div class="group-section-content">
                          @for (field of getSectionFields(group, section.name); track field.id) {
                            @if (groupForms[msmId][record.uuid]?.[field.id]) {
                              <div class="group-field" [class.group-field-with-hint]="field.ui_config?.hint" [class.mb-[30px]]="!field.ui_config?.hint && getFieldType(field) === 'display_only'" [class.mb-[40px]]="!field.ui_config?.hint && getFieldType(field) === 'boolean'" [class.mb-[20px]]="!field.ui_config?.hint && getFieldType(field) !== 'display_only' && getFieldType(field) !== 'boolean'" [formGroup]="groupForms[msmId][record.uuid][field.id]">
                                @switch (getFieldType(field)) {
                                  @case ('display_only') {
                                    <app-view-field
                                      [label]="field.label || field.name"
                                      [uom]="field.uom || ''"
                                      [value]="'' + (getGroupFieldValue(msmId, record.uuid, field.id) ?? '')"
                                    />
                                  }
                                  @case ('dropdown') {
                                    <app-dropdown
                                      [options]="getPickListOptions(field)"
                                      [parentFormGroup]="groupForms[msmId][record.uuid][field.id]"
                                      controlName="value"
                                      [label]="field.label || field.name"
                                      [hint]="field.ui_config?.hint || ''"
                                      [sentenceCase]="true"
                                      (checkForErrors)="onGroupFieldChange(msmId, record.uuid, field.id, materialGroups[material.material?.id])"
                                    />
                                  }
                                  @case ('number') {
                                    <app-number-input
                                      [isDecimalAllowed]="true"
                                      [decimalPlaces]="field.ui_config?.precision ?? 5"
                                      [maxLength]="24"
                                      [parentFormGroup]="groupForms[msmId][record.uuid][field.id]"
                                      controlName="value"
                                      [label]="(field.label || field.name) + (field.uom ? ' (' + field.uom + ')' : '')"
                                      [suffixText]="field.uom || ''"
                                      [hint]="field.ui_config?.hint || ''"
                                      (checkForErrors)="onGroupFieldChange(msmId, record.uuid, field.id, materialGroups[material.material?.id])"
                                    />
                                  }
                                  @case ('date') {
                                    <app-date-picker
                                      [parentFormGroup]="groupForms[msmId][record.uuid][field.id]"
                                      controlName="value"
                                      [label]="field.label || field.name"
                                      (checkForErrors)="onGroupFieldChange(msmId, record.uuid, field.id, materialGroups[material.material?.id])"
                                    />
                                  }
                                  @case ('time') {
                                    <app-time-select
                                      [parentFormGroup]="groupForms[msmId][record.uuid][field.id]"
                                      controlName="value"
                                      timePeriodControlName="timePeriod"
                                      amPmControlName="amPm"
                                      [label]="field.label || field.name"
                                      [hint]="field.ui_config?.hint || ''"
                                      (checkForErrors)="onGroupFieldChange(msmId, record.uuid, field.id, materialGroups[material.material?.id])"
                                    />
                                  }
                                  @case ('boolean') {
                                    <div class="boolean-field flex justify-between items-center">
                                      <span class="boolean-label text-type-13">{{ field.label || field.name }}</span>
                                      <mat-slide-toggle
                                        [checked]="groupForms[msmId][record.uuid][field.id]?.controls['value']?.value"
                                        (change)="groupForms[msmId][record.uuid][field.id]?.controls['value']?.setValue($event.checked); onGroupFieldChange(msmId, record.uuid, field.id, materialGroups[material.material?.id])"
                                      />
                                    </div>
                                  }
                                  @default {
                                    <app-text-input
                                      [parentFormGroup]="groupForms[msmId][record.uuid][field.id]"
                                      controlName="value"
                                      [label]="field.label || field.name"
                                      (checkForErrors)="onGroupFieldChange(msmId, record.uuid, field.id, materialGroups[material.material?.id])"
                                    />
                                  }
                                }
                              </div>
                            }
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }

          <!-- Add Record -->
          <div (click)="addRecord(material)" class="add-record-btn flex items-center gap-[8px] cursor-pointer">
            <mat-icon class="add-record-icon" svgIcon="plus" />
            Add record
          </div>

          <!-- Samples for this material (sample-collection-enabled orgs) -->
          @if (sampleRecordsEnabled && dailyReportId && bidItemMaterialSetId) {
            <app-embedded-sample-records
              (panelOpening)="done()"
              [projectId]="projectId"
              [dailyReportId]="dailyReportId"
              [bidItemId]="bidItemId"
              [bidItemMaterialSetId]="bidItemMaterialSetId"
              [materialId]="material.material?.id"
            />
          }
        </div>
      }
    </div>
  }

  <!-- Done Button -->
  <div class="action-section mt-[40px]">
    <app-primary-button
      (handleClick)="done()"
      class="w-full"
    >
      Done
    </app-primary-button>
  </div>
</div>
